import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../api";
import { apiCall } from "../api";
import { Btn } from "./common/FormElements";
import { Modal } from "./common/Modal";

const FREQ_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "12hours", label: "Every 12 Hours" },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: String(i).padStart(2, "0") + ":00",
}));

function TypeBadge({ type }) {
  const styles = {
    manual: "bg-blue-100 text-blue-700",
    auto: "bg-green-100 text-green-700",
    pre_restore: "bg-amber-100 text-amber-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[type] || "bg-gray-100 text-gray-600"}`}>
      {type === "pre_restore" ? "Pre-Restore" : type}
    </span>
  );
}

function formatSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
}

function formatDate(isoStr) {
  if (!isoStr) return "-";
  const d = new Date(isoStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function BackupRestore() {
  // Settings state
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Backup list
  const [backups, setBackups] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  // Actions
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // File upload restore
  const [uploadFile, setUploadFile] = useState(null);
  const [confirmUploadRestore, setConfirmUploadRestore] = useState(false);
  const [uploadRestoring, setUploadRestoring] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  function showToast(msg, type = "success") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }

  const fetchBackups = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await apiCall("GET", "/backup/list");
      setBackups(data);
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed to load backups", "error");
    } finally {
      setLoadingList(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await apiCall("GET", "/backup/settings");
      setSettings(data);
      setSettingsForm({
        auto_backup_enabled: data.auto_backup_enabled ?? true,
        frequency: data.frequency || "daily",
        hour: data.hour ?? 0,
        retention_count: data.retention_count ?? 7,
      });
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed to load settings", "error");
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchBackups();
  }, [fetchSettings, fetchBackups]);

  async function saveSettings() {
    setSettingsSaving(true);
    try {
      await apiCall("PUT", "/backup/settings", settingsForm);
      showToast("Backup settings saved");
      await fetchSettings();
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed to save settings", "error");
    } finally {
      setSettingsSaving(false);
    }
  }

  async function createBackup() {
    setCreating(true);
    try {
      await apiCall("POST", "/backup/create");
      showToast("Backup created successfully");
      await fetchBackups();
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed to create backup", "error");
    } finally {
      setCreating(false);
    }
  }

  async function downloadBackup(filename) {
    try {
      const resp = await axios.get(`${API}/backup/download/${filename}`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast("Download failed", "error");
    }
  }

  async function deleteBackup() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await apiCall("DELETE", `/backup/${confirmDelete}`);
      showToast(`Deleted ${confirmDelete}`);
      setConfirmDelete(null);
      await fetchBackups();
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed to delete", "error");
    } finally {
      setDeleting(false);
    }
  }

  async function restoreFromList() {
    if (!confirmRestore) return;
    setRestoring(true);
    try {
      // Download the backup file first, then POST it to restore
      const resp = await axios.get(`${API}/backup/download/${confirmRestore}`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const formData = new FormData();
      formData.append("file", resp.data, confirmRestore);
      const restoreResp = await axios.post(`${API}/backup/restore`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      const d = restoreResp.data;
      showToast(`Restored ${d.total_restored} records. ${d.warning}`);
      setConfirmRestore(null);
      await fetchBackups();
      setTimeout(() => window.location.reload(), 3000);
    } catch (e) {
      showToast(e.response?.data?.detail || "Restore failed", "error");
    } finally {
      setRestoring(false);
    }
  }

  async function restoreFromUpload() {
    if (!uploadFile) return;
    setUploadRestoring(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      const resp = await axios.post(`${API}/backup/restore`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      const d = resp.data;
      showToast(`Restored ${d.total_restored} records. ${d.warning}`);
      setConfirmUploadRestore(false);
      setUploadFile(null);
      await fetchBackups();
      setTimeout(() => window.location.reload(), 3000);
    } catch (e) {
      showToast(e.response?.data?.detail || "Restore failed", "error");
    } finally {
      setUploadRestoring(false);
    }
  }

  if (!settings) return <div className="text-sm text-gray-400 py-8 text-center">Loading backup settings...</div>;

  return (
    <div className="space-y-5" data-testid="backup-restore-tab">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${toast.type === "error" ? "bg-red-600 text-white" : "bg-green-600 text-white"}`} data-testid="backup-toast">
          {toast.msg}
        </div>
      )}

      {/* Section 1: Backup Settings */}
      <div className="bg-white rounded-xl border p-4" data-testid="backup-settings-card">
        <h3 className="font-semibold text-gray-800 mb-3 text-sm">Backup Settings</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1">Auto-backup</label>
            <button
              onClick={() => setSettingsForm(f => ({ ...f, auto_backup_enabled: !f.auto_backup_enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settingsForm.auto_backup_enabled ? "bg-blue-600" : "bg-gray-300"}`}
              data-testid="auto-backup-toggle"
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${settingsForm.auto_backup_enabled ? "translate-x-6" : "translate-x-1"}`} />
            </button>
            <span className="ml-2 text-xs text-gray-600">{settingsForm.auto_backup_enabled ? "Enabled" : "Disabled"}</span>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1">Frequency</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={settingsForm.frequency}
              onChange={e => setSettingsForm(f => ({ ...f, frequency: e.target.value }))}
              data-testid="backup-frequency-select"
            >
              {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1">Backup Hour (UTC)</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={settingsForm.hour}
              onChange={e => setSettingsForm(f => ({ ...f, hour: parseInt(e.target.value) }))}
              data-testid="backup-hour-select"
            >
              {HOUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1">Keep last N backups</label>
            <input
              type="number"
              min="1"
              max="30"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={settingsForm.retention_count}
              onChange={e => setSettingsForm(f => ({ ...f, retention_count: Math.max(1, Math.min(30, parseInt(e.target.value) || 1)) }))}
              data-testid="backup-retention-input"
            />
          </div>
        </div>
        <div className="mt-3">
          <Btn onClick={saveSettings} disabled={settingsSaving} testId="save-backup-settings-btn">
            {settingsSaving ? "Saving..." : "Save Settings"}
          </Btn>
          {settings.last_auto_backup_at && (
            <span className="ml-3 text-xs text-gray-400">Last auto-backup: {formatDate(settings.last_auto_backup_at)}</span>
          )}
        </div>
      </div>

      {/* Section 2: Manual Backup */}
      <div className="bg-white rounded-xl border p-4" data-testid="manual-backup-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800 text-sm">Manual Backup</h3>
            <p className="text-xs text-gray-500 mt-0.5">Create a full database snapshot right now</p>
          </div>
          <button
            onClick={createBackup}
            disabled={creating}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="create-backup-btn"
          >
            {creating && <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {creating ? "Creating..." : "Create Backup Now"}
          </button>
        </div>
      </div>

      {/* Section 3: Backup List */}
      <div className="bg-white rounded-xl border overflow-hidden" data-testid="backup-list-card">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-800 text-sm">Available Backups</h3>
        </div>
        {loadingList ? (
          <div className="text-sm text-gray-400 py-8 text-center">Loading...</div>
        ) : backups.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center" data-testid="backup-list-empty">
            No backups yet. Create your first backup above.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                {["Filename", "Type", "Created At", "Size", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {backups.map(b => (
                <tr key={b.filename} className="border-b hover:bg-gray-50" data-testid={`backup-row-${b.filename}`}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700 max-w-[200px] truncate">{b.filename}</td>
                  <td className="px-4 py-3"><TypeBadge type={b.type} /></td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(b.created_at)}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{formatSize(b.file_size)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => downloadBackup(b.filename)} className="text-blue-500 hover:text-blue-700 text-xs font-medium" data-testid={`download-${b.filename}`}>Download</button>
                      <button onClick={() => setConfirmRestore(b.filename)} className="text-amber-500 hover:text-amber-700 text-xs font-medium" data-testid={`restore-${b.filename}`}>Restore</button>
                      <button onClick={() => setConfirmDelete(b.filename)} className="text-red-400 hover:text-red-600 text-xs font-medium" data-testid={`delete-${b.filename}`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Section 4: Restore from File */}
      <div className="bg-white rounded-xl border p-4" data-testid="restore-upload-card">
        <h3 className="font-semibold text-gray-800 text-sm mb-2">Restore from File</h3>
        <p className="text-xs text-gray-500 mb-3">Upload a previously downloaded backup JSON file to restore</p>
        <div className="flex items-center gap-3">
          <label className="flex-1 flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
            <span className="text-sm text-gray-600">{uploadFile ? uploadFile.name : "Choose .json backup file..."}</span>
            <input
              type="file"
              accept=".json"
              className="hidden"
              onChange={e => setUploadFile(e.target.files[0] || null)}
              data-testid="restore-file-input"
            />
          </label>
          {uploadFile && (
            <button onClick={() => setUploadFile(null)} className="text-xs text-red-400 hover:text-red-600">Clear</button>
          )}
          <button
            onClick={() => setConfirmUploadRestore(true)}
            disabled={!uploadFile || uploadRestoring}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium text-sm hover:bg-amber-700 transition-colors disabled:opacity-50"
            data-testid="upload-restore-btn"
          >
            {uploadRestoring ? "Restoring..." : "Upload & Restore"}
          </button>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {confirmDelete && (
        <Modal title="Delete Backup" onClose={() => setConfirmDelete(null)}>
          <p className="text-sm text-gray-700 mb-4">
            Delete backup <strong className="font-mono text-xs">{confirmDelete}</strong>? This cannot be undone.
          </p>
          <div className="flex gap-2">
            <Btn onClick={deleteBackup} disabled={deleting} variant="danger" testId="confirm-delete-backup-btn">
              {deleting ? "Deleting..." : "Delete"}
            </Btn>
            <Btn onClick={() => setConfirmDelete(null)} variant="ghost">Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Restore from List Confirm Modal */}
      {confirmRestore && (
        <Modal title="Restore Backup" onClose={() => !restoring && setConfirmRestore(null)}>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800 font-medium">Warning: This will REPLACE ALL current data with this backup.</p>
            <p className="text-xs text-amber-700 mt-1">A pre-restore backup will be created automatically so you can recover if needed.</p>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            Restore from: <strong className="font-mono text-xs">{confirmRestore}</strong>
          </p>
          <div className="flex gap-2">
            <button
              onClick={restoreFromList}
              disabled={restoring}
              className="px-4 py-1.5 bg-amber-600 text-white rounded-lg font-medium text-sm hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="confirm-restore-backup-btn"
            >
              {restoring && <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {restoring ? "Restoring..." : "Yes, Restore"}
            </button>
            <Btn onClick={() => setConfirmRestore(null)} variant="ghost" disabled={restoring}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Upload Restore Confirm Modal */}
      {confirmUploadRestore && (
        <Modal title="Restore from File" onClose={() => !uploadRestoring && setConfirmUploadRestore(false)}>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800 font-medium">Warning: This will REPLACE ALL current data with the uploaded backup.</p>
            <p className="text-xs text-amber-700 mt-1">A pre-restore backup will be created automatically so you can recover if needed.</p>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            File: <strong>{uploadFile?.name}</strong> ({formatSize(uploadFile?.size)})
          </p>
          <div className="flex gap-2">
            <button
              onClick={restoreFromUpload}
              disabled={uploadRestoring}
              className="px-4 py-1.5 bg-amber-600 text-white rounded-lg font-medium text-sm hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="confirm-upload-restore-btn"
            >
              {uploadRestoring && <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {uploadRestoring ? "Restoring..." : "Yes, Restore"}
            </button>
            <Btn onClick={() => setConfirmUploadRestore(false)} variant="ghost" disabled={uploadRestoring}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
